import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  GoogleAuthProvider,
  deleteUser,
  linkWithCredential,
  onIdTokenChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type AuthCredential,
  type User,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { firebaseAuth } from '../firebase'
import { callConsumeInviteKeyForCurrentUser, callRedeemInviteKey } from '../functions'

/**
 * Thrown by signInWithGoogle when the email already has an account with a
 * different sign-in method (i.e. a pre-existing password account). The UI
 * should prompt the user for their existing password and call
 * completePendingGoogleLink so the Google credential gets attached to the
 * existing account — after that they can sign in with either method.
 */
export class PendingGoogleLinkError extends Error {
  email: string
  pendingCredential: AuthCredential
  constructor(email: string, pendingCredential: AuthCredential) {
    super(
      `This email already has a password account. Enter your password to link Google sign-in.`
    )
    this.name = 'PendingGoogleLinkError'
    this.email = email
    this.pendingCredential = pendingCredential
  }
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  isBeta: boolean
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  /**
   * After a PendingGoogleLinkError, sign in with the existing password and
   * attach the previously captured Google credential to that account.
   */
  completePendingGoogleLink: (
    email: string,
    password: string,
    pendingCredential: AuthCredential
  ) => Promise<void>
  signOut: () => Promise<void>
  registerWithInviteKey: (args: {
    code: string
    email: string
    password: string
    displayName?: string
  }) => Promise<void>
  registerWithGoogle: (code: string) => Promise<void>
  refreshBetaClaim: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isBeta, setIsBeta] = useState(false)

  useEffect(() => {
    // onIdTokenChanged (not onAuthStateChanged) so the effect re-runs after
    // a token refresh — that's where the beta custom claim lands after a
    // fresh redemption, since setCustomUserClaims requires a new ID token.
    const unsub = onIdTokenChanged(firebaseAuth, async (u) => {
      setUser(u)
      if (u) {
        const token = await u.getIdTokenResult()
        setIsBeta(token.claims.beta === true)
      } else {
        setIsBeta(false)
      }
      setLoading(false)
    })
    return unsub
  }, [])

  const refreshBetaClaim = useCallback(async () => {
    const current = firebaseAuth.currentUser
    if (!current) return
    const token = await current.getIdTokenResult(true)
    setIsBeta(token.claims.beta === true)
  }, [])

  const value: AuthContextValue = {
    user,
    loading,
    isBeta,
    signIn: async (email, password) => {
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password)
    },
    signInWithGoogle: async () => {
      // Plain Google sign-in used from /signin for returning testers.
      // Does NOT consume an invite code — if the user hasn't redeemed
      // one yet they land signed in but without the beta claim, and
      // every downstream function call will reject them. The /register
      // flow is the only path that mints a fresh beta account.
      const provider = new GoogleAuthProvider()
      try {
        await signInWithPopup(firebaseAuth, provider)
      } catch (err) {
        // Firebase returns this when the email has a pre-existing
        // password account. We capture the pending Google credential and
        // surface it back to the UI; after the user re-authenticates
        // with their existing password, completePendingGoogleLink links
        // the Google credential to the same account, so both methods
        // work going forward.
        if (
          err instanceof FirebaseError &&
          err.code === 'auth/account-exists-with-different-credential'
        ) {
          const pendingCredential = GoogleAuthProvider.credentialFromError(err)
          const email = (err.customData?.email as string | undefined) ?? ''
          if (pendingCredential && email) {
            throw new PendingGoogleLinkError(email, pendingCredential)
          }
        }
        throw err
      }
      await firebaseAuth.currentUser?.getIdToken(true)
    },
    completePendingGoogleLink: async (email, password, pendingCredential) => {
      // Re-authenticate with the existing password account, then attach
      // the Google credential we captured earlier. linkWithCredential
      // mutates the current user so subsequent Google sign-ins for this
      // email succeed without ever hitting the duplicate-account error
      // again.
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password)
      const current = firebaseAuth.currentUser
      if (!current) throw new Error('No signed-in user after password verification')
      await linkWithCredential(current, pendingCredential)
      await current.getIdToken(true)
    },
    signOut: async () => {
      await fbSignOut(firebaseAuth)
    },
    registerWithInviteKey: async ({ code, email, password, displayName }) => {
      const result = await callRedeemInviteKey({ code, email, password, displayName })
      const { customToken } = result.data
      await signInWithCustomToken(firebaseAuth, customToken)
      // The custom token was minted with the beta claim; force-refresh the
      // ID token so onIdTokenChanged picks it up on the next tick.
      await firebaseAuth.currentUser?.getIdToken(true)
    },
    registerWithGoogle: async (code) => {
      // Option B Google sign-up: pop the Google OAuth dialog, then
      // immediately try to consume the invite code for the newly
      // created Firebase user. If consumption fails (code invalid,
      // expired, already used), we delete the freshly-created Firebase
      // user and re-throw the error so the UI can show it. This keeps
      // the Firebase Auth tenant clean of orphan accounts that have
      // no beta claim and no way to recover.
      const provider = new GoogleAuthProvider()
      const credential = await signInWithPopup(firebaseAuth, provider)
      const newUser = credential.user
      try {
        await callConsumeInviteKeyForCurrentUser({ code })
        await newUser.getIdToken(true)
      } catch (err) {
        try {
          await deleteUser(newUser)
        } catch (deleteErr) {
          // If deletion fails (e.g. requires recent sign-in, network),
          // at least sign out so the user isn't trapped. The orphan
          // account can be cleaned up manually from the Firebase
          // console; we log the issue.
          console.error('[auth] Failed to delete orphan Google account:', deleteErr)
          await fbSignOut(firebaseAuth)
        }
        if (err instanceof FirebaseError) {
          throw new Error(err.message)
        }
        throw err
      }
    },
    refreshBetaClaim,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
